import { Alert } from 'antd';
import type { MapTileProvider } from './useNowcastingState';

// ============================================================================
// Types
// ============================================================================

export interface MapAlertsProps {
  error: string | null;
  loading: boolean;
  selectedDateRange: { start: Date; end: Date } | null;
  mapTileProvider: MapTileProvider;
  tilesLength: number;
  theme: string;
  onErrorClose: () => void;
}

// ============================================================================
// Styles
// ============================================================================

const alertContainerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 2000,
  maxWidth: '500px',
};

// ============================================================================
// Component
// ============================================================================

/**
 * Map alert overlays for error states and informational messages.
 * 
 * Displays:
 * - Error overlay when tile loading fails
 * - "Select Time Range" overlay when no date range is selected
 * - "No Radar Data" overlay when no tiles are available for the selected range
 */
export function MapAlerts({
  error,
  loading,
  selectedDateRange,
  mapTileProvider,
  tilesLength,
  theme,
  onErrorClose,
}: MapAlertsProps) {
  // Error Overlay
  if (error && !loading) {
    return (
      <div style={alertContainerStyle}>
        <Alert
          title="Error Loading Tiles"
          description={error}
          type="error"
          showIcon
          closable={{ onClose: onErrorClose }}
        />
      </div>
    );
  }

  // No Data Selected Overlay
  if (!selectedDateRange && !loading) {
    return (
      <div style={alertContainerStyle}>
        <Alert
          title="Select Time Range"
          description="Use the timeline above to select a time range and load radar tiles"
          type="info"
          showIcon
        />
      </div>
    );
  }

  // No Tiles Available Overlay (only for radar mode)
  if (selectedDateRange && !loading && !error && (mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && tilesLength === 0) {
    return (
      <div style={alertContainerStyle}>
        <Alert
          title="No Radar Data"
          description="No radar tiles available for the selected time range"
          type="warning"
          showIcon
          style={{
            backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
            border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(10px)',
          }}
        />
      </div>
    );
  }

  return null;
}
